<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl tw-min-h-80">
          <v-card-text>
            <v-breadcrumbs :items="breadcrumbItems">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :disabled="item.disabled"
                  :exact="true"
                  :to="item.to"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>
            <div class="tw-flex tw-justify-between">
              <div
                class="tw-mx-5 tw-font-bold tw-text-xl tw-text-black tw-tracking-wide"
              >
                Department Settings
              </div>
              <v-btn
                color="primary"
                @click="saveAllChanges"
                :disabled="!hasChanges || saving || hasInvalidEmails"
                :loading="saving"
                class="tw-mr-5"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </div>
            <div class="tw-mx-5">
            <v-overlay :value="isLoading" absolute color="white" opacity="0.8">
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
              ></v-progress-circular>
              <div class="mt-3 text-center">Loading...</div>
            </v-overlay>

           

            <v-simple-table>
              <thead>
                <tr>
                  <th>No.</th>
                  <!-- <th>Department ID</th> -->
                  <th>Department Name</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Default Email</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!departments.length && !isLoading">
                  <td :colspan="5" class="text-center py-8">No departments found</td>
                </tr>
                <tr v-for="(dept, idx) in departments" :key="dept.id">
                  <td>{{ startIndex + idx + 1 }}</td>
                  <!-- <td>
                    <v-text-field
                      :value="dept.id"
                      disabled
                      dense
                      outlined
                      hide-details
                      class="my-2"
                    ></v-text-field>
                  </td> -->
                  <td>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          v-on="on"
                          :value="dept.name"
                          disabled
                          dense
                          outlined
                          hide-details
                          class="my-2"
                        ></v-text-field>
                      </template>
                      <span>{{ dept.name }}</span>
                    </v-tooltip>
                  </td>

                  <td>
                    <v-switch
                      :input-value="dept.isActive"
                      :label="dept.isActive ? 'Active' : 'Inactive'"
                      disabled
                      inset
                      dense
                      hide-details
                      class="my-2"
                    ></v-switch>
                  </td>
                  <td>
                    <v-text-field
                      :value="dept.source || ''"
                      label="Source"
                      disabled
                      dense
                      outlined
                      hide-details
                      class="my-2"
                    ></v-text-field>
                  </td>
                  <td class="py-2">
                    <v-autocomplete
                      v-model="dept.defaultEmail"
                      :items="companyUsers"
                      label="Email Address"
                      dense
                      outlined
                      placeholder="Select or type email address"
                      @input="markAsChanged(dept.id)"
                      :error-messages="getEmailError(dept)"
                      :error="!isEmailValid(dept)"
                      hide-details="auto"
                      clearable
                    ></v-autocomplete>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- Pagination Navigation -->
            <div class="tw-flex tw-justify-between tw-items-center tw-mt-6 tw-gap-2 mr-5 ml-5">

              <div class="tw-flex tw-items-center tw-gap-2">
                <span class="tw-text-sm tw-text-gray-600">Rows per page:</span>
                <v-select
                  v-model="pagination.itemsPerPage"
                  :items="[10, 25, 50, 100]"
                  @change="changeItemsPerPage"
                  dense
                  outlined
                  hide-details
                  style="width: 100px;"
                ></v-select>
              </div>




              <div class="tw-flex tw-items-center tw-gap-2">
                <v-btn
                  icon
                  small
                  :disabled="!pagination.hasPrevPage || isLoading"
                  @click="goToPreviousPage"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <span class="tw-text-sm tw-text-gray-600">
                  Page {{ pagination.currentPage }} of {{ totalPages }}
                </span>

                <v-btn
                  icon
                  small
                  :disabled="!pagination.hasNextPage || isLoading"
                  @click="goToNextPage"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>

              <div class="tw-text-sm tw-text-gray-600">
                Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalItems }}
              </div>
            </div>

            </div>
          </v-card-text>
        </v-card>

        <!-- Snackbar for notifications -->
        <v-snackbar
          v-model="snackbar.show"
          :color="snackbar.color"
          :timeout="4000"
          top
          right
        >
          <v-icon left>{{ snackbar.icon }}</v-icon>
          {{ snackbar.msg }}
          <template v-slot:action="{ attrs }">
            <v-btn
              text
              v-bind="attrs"
              @click="snackbar.show = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState, mapActions } from 'vuex';
import fc from "@/firebaseConfig";
import firebase from "firebase/app";
import getRole from "@/libs/getRole";

export default {
  name: 'SettingDepartment',
  components: {
    Admin,
  },
  data() {
    return {
      isLoading: false,
      saving: false,
      savingDept: null,
      changedDepartments: [], // Changed from Set to Array for Vue 2 reactivity
      originalEmails: {}, // Track original email values
      companyUsers: [], // Store company users emails
      pagination: {
        currentPage: 1,
        itemsPerPage: 25,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false,
        lastDoc: null,
        firstDoc: null,
        pageCache: {} // Cache for pages
      },
      breadcrumbItems: [
        {
          text: "ตั้งค่า",
          disabled: false,
          to: { name: "Company.Settings" },
        },
        {
          text: "Department Settings",
          disabled: true,
        },
      ],
      snackbar: {
        show: false,
        msg: '',
        color: 'success',
        icon: 'mdi-check-circle-outline'
      }
    };
  },
  computed: {
    ...mapState({
      departments: state => state.fixedAssets.departments,
    }),
    hasChanges() {
      return this.changedDepartments.length > 0;
    },
    hasInvalidEmails() {
      const changedDepts = this.departments.filter(dept =>
        this.changedDepartments.includes(dept.id)
      );
      return changedDepts.some(dept => !this.isEmailValid(dept));
    },
    totalItems() {
      return this.pagination.totalItems || 0;
    },
    startIndex() {
      return (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
    },
    endIndex() {
      const end = this.startIndex + this.departments.length;
      return end;
    },
    totalPages() {
      return Math.ceil(this.pagination.totalItems / this.pagination.itemsPerPage) || 1;
    }
  },
  watch: {
    departments: {
      handler(newDepartments) {
        // Store original email values when departments are loaded/updated
        if (newDepartments && newDepartments.length > 0) {
          newDepartments.forEach(dept => {
            if (!(dept.id in this.originalEmails)) {
              // Use $set for Vue 2 reactivity
              this.$set(this.originalEmails, dept.id, dept.defaultEmail || '');
            }
          });
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    markAsChanged(deptId) {
      // Find the department
      const dept = this.departments.find(d => d.id === deptId);
      if (!dept) return;

      // Get original and current values
      const originalEmail = this.originalEmails[deptId] || '';
      const currentEmail = dept.defaultEmail || '';

      // Check if value actually changed from original
      if (currentEmail !== originalEmail) {
        // Add to changed list if not already there
        if (!this.changedDepartments.includes(deptId)) {
          this.changedDepartments.push(deptId);
        }
      } else {
        // Value reverted to original, remove from changed list
        const index = this.changedDepartments.indexOf(deptId);
        if (index > -1) {
          this.changedDepartments.splice(index, 1);
        }
      }
    },

    isChanged(deptId) {
      return this.changedDepartments.includes(deptId);
    },

    isEmailValid(dept) {
      // If no email provided, it's valid (optional field)
      if (!dept.defaultEmail || dept.defaultEmail.trim() === '') {
        return true;
      }
      // Check email format
      const emailRegex = /.+@.+\..+/;
      return emailRegex.test(dept.defaultEmail);
    },

    getEmailError(dept) {
      if (!dept.defaultEmail || dept.defaultEmail.trim() === '') {
        return '';
      }
      if (!this.isEmailValid(dept)) {
        return 'Please enter a valid email address';
      }
      return '';
    },

    async saveDepartment(dept) {
      if (!dept.id) return;

      // Validate email before saving
      if (!this.isEmailValid(dept)) {
        this.snackbar = {
          show: true,
          msg: 'Please enter a valid email address before saving',
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        };
        return;
      }

      this.savingDept = dept.id;
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId');

      try {
        await fc.companies
          .doc(companyId)
          .collection("FixedAssetDepartment")
          .doc(dept.id)
          .update({
            defaultEmail: dept.defaultEmail || '',
            updatedAt: firebase.firestore.FieldValue.serverTimestamp()
          });

        // Update original email value after successful save
        this.$set(this.originalEmails, dept.id, dept.defaultEmail || '');

        // Remove from changed list
        const index = this.changedDepartments.indexOf(dept.id);
        if (index > -1) {
          this.changedDepartments.splice(index, 1);
        }

        this.snackbar = {
          show: true,
          msg: `Department "${dept.name}" updated successfully!`,
          color: 'success',
          icon: 'mdi-check-circle-outline'
        };
      } catch (error) {
        console.error('Error updating department:', error);
        this.snackbar = {
          show: true,
          msg: `Error updating department: ${error.message}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        };
      } finally {
        this.savingDept = null;
      }
    },

    async saveAllChanges() {
      if (!this.hasChanges) return;

      this.saving = true;
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId');

      let successCount = 0;
      let failCount = 0;
      const errors = [];

      try {
        const changedDepts = this.departments.filter(dept =>
          this.changedDepartments.includes(dept.id)
        );

        for (const dept of changedDepts) {
          // Validate email before saving
          if (!this.isEmailValid(dept)) {
            failCount++;
            errors.push(`${dept.name}: Invalid email address`);
            continue;
          }

          try {
            await fc.companies
              .doc(companyId)
              .collection("FixedAssetDepartment")
              .doc(dept.id)
              .update({
                defaultEmail: dept.defaultEmail || '',
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
              });

            // Update original email value after successful save
            this.$set(this.originalEmails, dept.id, dept.defaultEmail || '');

            // Remove from changed list
            const index = this.changedDepartments.indexOf(dept.id);
            if (index > -1) {
              this.changedDepartments.splice(index, 1);
            }

            successCount++;
          } catch (error) {
            failCount++;
            errors.push(`${dept.name}: ${error.message}`);
          }
        }

        // Show result
        if (failCount === 0) {
          this.snackbar = {
            show: true,
            msg: `All ${successCount} department(s) updated successfully!`,
            color: 'success',
            icon: 'mdi-check-circle-outline'
          };
        } else if (successCount === 0) {
          this.snackbar = {
            show: true,
            msg: `Failed to update all departments. ${errors.join('; ')}`,
            color: 'error',
            icon: 'mdi-alert-circle-outline'
          };
        } else {
          this.snackbar = {
            show: true,
            msg: `Updated ${successCount} department(s), ${failCount} failed. Errors: ${errors.join('; ')}`,
            color: 'warning',
            icon: 'mdi-alert-outline'
          };
        }
      } catch (error) {
        console.error('Error saving changes:', error);
        this.snackbar = {
          show: true,
          msg: `Error saving changes: ${error.message}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        };
      } finally {
        this.saving = false;
      }
    },

    async loadDepartments(direction = 'next') {
      this.isLoading = true;
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId');

      try {
        const result = await this.fetchDepartmentsPaginated({
          companyId,
          limit: this.pagination.itemsPerPage,
          direction,
          lastDoc: this.pagination.lastDoc,
          firstDoc: this.pagination.firstDoc
        });

        if (result) {
          this.pagination.totalItems = result.totalItems || 0;
          this.pagination.hasNextPage = result.hasNextPage || false;
          this.pagination.hasPrevPage = result.hasPrevPage || false;
          this.pagination.lastDoc = result.lastDoc || null;
          this.pagination.firstDoc = result.firstDoc || null;
        }
      } catch (error) {
        console.error('Error loading departments:', error);
        this.snackbar = {
          show: true,
          msg: `Error loading departments: ${error.message}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        };
      } finally {
        this.isLoading = false;
      }
    },

    async goToNextPage() {
      if (!this.pagination.hasNextPage) return;
      this.pagination.currentPage++;
      await this.loadDepartments('next');
    },

    async goToPreviousPage() {
      if (!this.pagination.hasPrevPage || this.pagination.currentPage === 1) return;
      this.pagination.currentPage--;
      await this.loadDepartments('prev');
    },

    async changeItemsPerPage() {
      // Reset to first page when changing items per page
      this.pagination.currentPage = 1;
      this.pagination.lastDoc = null;
      this.pagination.firstDoc = null;
      await this.loadDepartments('next');
    },

    async fetchDepartmentsPaginated({ companyId, limit, direction, lastDoc, firstDoc }) {
      // Build query
      let query = fc.companies
        .doc(companyId)
        .collection("FixedAssetDepartment")
        .orderBy('name');

      // Apply pagination
      if (direction === 'next' && lastDoc) {
        query = query.startAfter(lastDoc);
      } else if (direction === 'prev' && firstDoc) {
        query = query.endBefore(firstDoc).limitToLast(limit);
      }

      if (direction === 'next') {
        query = query.limit(limit);
      }

      const snapshot = await query.get();

      // Get total count (for first load only)
      let totalItems = this.pagination.totalItems;
      if (this.pagination.currentPage === 1 && direction === 'next') {
        const countSnapshot = await fc.companies
          .doc(companyId)
          .collection("FixedAssetDepartment")
          .get();
        totalItems = countSnapshot.size;
      }

      // Map departments
      const departments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Store in Vuex
      this.$store.commit('fixedAssets/setDepartments', departments);

      // Determine pagination state
      const hasNextPage = snapshot.docs.length === limit;
      const hasPrevPage = this.pagination.currentPage > 1;

      return {
        departments,
        totalItems,
        hasNextPage,
        hasPrevPage,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        firstDoc: snapshot.docs[0]
      };
    },

    async fetchCompanyUsers() {
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId');

      try {
        const snapshot = await fc.db
          .collection("CompaniesUsers")
          .where("companyID", "==", companyId)
          .get();

        // Extract unique email addresses for users with 'owner' or 'user' role
        const emailsSet = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const userRole = getRole(data);
          // Only include users with 'owner' or 'user' role
          if (data.user && data.user.email && (userRole === 'owner' || userRole === 'user')) {
            emailsSet.add(data.user.email);
          }
        });

        this.companyUsers = Array.from(emailsSet).sort();
      } catch (error) {
        console.error('Error fetching company users:', error);
        this.snackbar = {
          show: true,
          msg: `Error loading company users: ${error.message}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        };
      }
    }
  },
  mounted() {
    this.loadDepartments('next');
    this.fetchCompanyUsers();
  }
}
</script>

<style lang="scss" scoped>

</style>